import { useState } from 'react';
import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import { useResource } from '../lib/useResource.js';
import { post } from '../lib/api.js';
import { formatDateTime } from '../lib/format.js';
import {
  Alert,
  Badge,
  Card,
  Empty,
  ErrorBox,
  Field,
  Modal,
  PageHeader,
  useSubmit,
  useToast,
} from '../components/ui.js';
import { useSession } from '../lib/session.js';
import { Tabela } from '../components/Tabela.js';
import { NovoLembrete } from './reminders/NovoLembrete.js';
import { EditorSequencia } from './reminders/EditorSequencia.js';

interface Cycle {
  id: string;
  status: string;
  purpose: string;
  current_step: number;
  next_send_at: string | null;
  pause_reason: string | null;
  awaiting_reason: string | null;
  customer_name: string;
  make?: string | null;
  model?: string | null;
  model_year?: number | null;
}

interface Step {
  stepNumber: number;
  offsetAmount: number;
  offsetUnit: string;
  messageBody: string;
}
interface Template {
  id: string;
  name: string;
  purpose: string;
  status: string;
  priority: number;
  version: number;
  first_offset_amount: number;
  first_offset_unit: string;
  steps: Step[];
}

const MOTIVOS: Record<string, string> = {
  inbound_message: 'o cliente respondeu',
  active_booking: 'existe reserva ativa',
  human_active: 'atendimento humano assumiu',
  vehicle_owner_changed: 'o dono do veículo mudou',
  vehicle_transferred: 'veículo transferido',
  send_failed: 'falha no envio',
  delivery_unknown: 'entrega incerta',
  expired_step: 'a etapa venceu',
  render_failed: 'falta dado na mensagem',
  no_contact_channel: 'cliente sem telefone',
};

const UNIDADE: Record<string, string> = {
  days: 'dias',
  weeks: 'semanas',
  months: 'meses',
  years: 'anos',
};

export function RemindersLayout() {
  const { can } = useSession();
  const toast = useToast();
  const [novo, setNovo] = useState(false);
  const clientes = useResource<{ customers: { id: string; name: string }[] }>('/api/customers?term=');
  const [recarga, setRecarga] = useState(0);

  return (
    <>
      <PageHeader
        title="Lembretes e retornos"
        description="O contato de retorno é um prazo comercial. Ele não afirma, sozinho, que o veículo precisa de manutenção."
        actions={
          can('reminders.write') ? (
            <button type="button" className="primary" onClick={() => setNovo(true)}>
              Novo lembrete
            </button>
          ) : null
        }
      />

      <nav className="tabs" aria-label="Seções de lembretes">
        <NavLink to="/lembretes" end>
          Sequências
        </NavLink>
        <NavLink to="/lembretes/proximos">Próximos envios</NavLink>
        <NavLink to="/lembretes/pausados">Pausados e aguardando ação</NavLink>
        <NavLink to="/lembretes/intervalos">Intervalos de retorno</NavLink>
        <NavLink to="/lembretes/historico">Histórico</NavLink>
      </nav>

      <Outlet context={{ recarga }} />

      {novo ? (
        <NovoLembreteAvulso
          clientes={clientes.data?.customers ?? []}
          onClose={() => setNovo(false)}
          onCreated={() => {
            setNovo(false);
            setRecarga((r) => r + 1);
            toast('Lembrete criado.');
          }}
        />
      ) : null}
    </>
  );
}

function NovoLembreteAvulso({
  clientes,
  onClose,
  onCreated,
}: {
  clientes: { id: string; name: string }[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [customerId, setCustomerId] = useState('');
  const veiculos = useResource<{ vehicles: [] }>(customerId ? `/api/customers/${customerId}/vehicles` : null);

  if (!customerId) {
    return (
      <Modal title="Novo lembrete" onClose={onClose}>
        <Field label="Cliente" required>
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} autoFocus>
            <option value="">Escolha o cliente</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <p className="hint">O lembrete avulso não exige veículo nem serviço concluído.</p>
      </Modal>
    );
  }
  return (
    <NovoLembrete
      customerId={customerId}
      vehicles={veiculos.data?.vehicles ?? []}
      onClose={onClose}
      onCreated={onCreated}
    />
  );
}

// ------------------------------------------------------------- sequências

export function Sequencias() {
  const { can } = useSession();
  const toast = useToast();
  const templates = useResource<{ templates: Template[] }>('/api/reminder-templates');
  const [aplicando, setAplicando] = useState<Template | null>(null);
  const [criando, setCriando] = useState(false);

  return (
    <>
      <ErrorBox error={templates.error} />
      {can('reminders.write') ? (
        <div className="toolbar">
          <button type="button" className="primary" onClick={() => setCriando(true)}>
            Nova sequência
          </button>
          <span className="muted small">
            Uma sequência precisa estar publicada para programar retornos.
          </span>
        </div>
      ) : null}
      {templates.data && templates.data.templates.length === 0 ? (
        <Card>
          <Empty title="Nenhuma sequência cadastrada.">
            Sem sequência ativa, registrar um serviço não programa retorno.
          </Empty>
        </Card>
      ) : null}

      {(templates.data?.templates ?? []).map((template) => (
        <Card
          key={template.id}
          title={template.name}
          description={`Finalidade ${template.purpose} · versão ${template.version} · primeiro contato em ${template.first_offset_amount} ${UNIDADE[template.first_offset_unit] ?? template.first_offset_unit}`}
          actions={
            <>
              <Badge tone={template.status === 'active' ? 'ok' : 'attention'}>
                {template.status === 'active' ? 'ativa' : template.status === 'draft' ? 'rascunho' : 'pausada'}
              </Badge>
              {can('reminders.write') && template.status === 'draft' ? (
                <button
                  type="button"
                  className="small primary"
                  onClick={async () => {
                    await post(`/api/reminder-templates/${template.id}/activate`, {});
                    templates.reload();
                    toast('Sequência publicada.');
                  }}
                >
                  Publicar
                </button>
              ) : null}
              {can('reminders.write') && template.status === 'active' ? (
                <button type="button" className="small" onClick={() => setAplicando(template)}>
                  Aplicar a ciclos ativos
                </button>
              ) : null}
            </>
          }
        >
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {template.steps.map((step) => (
              <li key={step.stepNumber} style={{ marginBottom: 10 }}>
                <div className="small muted">
                  {step.stepNumber === 1
                    ? 'No prazo do primeiro contato'
                    : `${step.offsetAmount} ${UNIDADE[step.offsetUnit] ?? step.offsetUnit} após o envio aceito da etapa anterior`}
                </div>
                <div className="preview-box" style={{ marginTop: 4 }}>
                  {step.messageBody}
                </div>
              </li>
            ))}
          </ol>
        </Card>
      ))}

      {criando ? (
        <EditorSequencia
          onClose={() => setCriando(false)}
          onCreated={() => {
            setCriando(false);
            templates.reload();
            toast('Sequência criada.');
          }}
        />
      ) : null}

      {aplicando ? (
        <AplicarAtivos
          template={aplicando}
          onClose={() => setAplicando(null)}
          onApplied={() => {
            setAplicando(null);
            templates.reload();
            toast('Sequência aplicada aos ciclos ativos.');
          }}
        />
      ) : null}
    </>
  );
}

function AplicarAtivos({
  template,
  onClose,
  onApplied,
}: {
  template: Template;
  onClose: () => void;
  onApplied: () => void;
}) {
  const preview = useResource<{ affectedCycles: number; sample: { cycleId: string; customerName: string; nextSendAt: string | null }[] }>(
    `/api/reminder-templates/${template.id}/apply-preview`,
  );
  const [reason, setReason] = useState('');
  const { submit, saving, error } = useSubmit(async () => {
    await post(`/api/reminder-templates/${template.id}/apply`, { reason });
    onApplied();
  });

  return (
    <Modal
      title="Aplicar aos ciclos ativos"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="submit"
            form="form-aplicar"
            className="primary"
            disabled={saving || reason.trim().length < 3}
          >
            {saving ? 'Aplicando…' : 'Aplicar'}
          </button>
        </>
      }
    >
      <form id="form-aplicar" onSubmit={submit}>
        <ErrorBox error={error} />
        <Alert tone="warning" title={`${preview.data?.affectedCycles ?? 0} ciclos serão recalculados`}>
          Etapas já enviadas são preservadas e nunca reenviadas. Só as etapas ainda não enviadas
          mudam.
        </Alert>
        {preview.data?.sample.length ? (
          <ul className="list" style={{ border: '1px solid var(--gl-border)', borderRadius: 6, marginBottom: 14 }}>
            {preview.data.sample.map((s) => (
              <li key={s.cycleId}>
                <div className="list-main">
                  <div className="list-title">{s.customerName}</div>
                </div>
                <span className="list-when">{formatDateTime(s.nextSendAt)}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <Field label="Motivo" required hint="Fica registrado na auditoria.">
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} required />
        </Field>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------- próximos envios

export function ProximosEnvios() {
  const { recarga } = useOutletContext<{ recarga: number }>();
  const cycles = useResource<{ cycles: Cycle[] }>('/api/reminders/upcoming', [recarga]);

  return (
    <Card tight>
      <ErrorBox error={cycles.error} />
      <Tabela
        linhas={cycles.data?.cycles ?? []}
        chave={(c) => c.id}
        vazio="Nenhum envio programado."
        colunas={[
          { chave: 'cliente', rotulo: 'Cliente', tipo: 'principal', render: (c) => c.customer_name },
          {
            chave: 'veiculo',
            rotulo: 'Veículo',
            render: (c) => [c.make, c.model, c.model_year].filter(Boolean).join(' ') || '—',
          },
          {
            chave: 'finalidade',
            rotulo: 'Finalidade',
            ocultarNoCelular: true,
            render: (c) => (c.purpose.startsWith('standalone:') ? 'lembrete avulso' : c.purpose),
          },
          { chave: 'etapa', rotulo: 'Etapa', tipo: 'num', render: (c) => (c.current_step > 0 ? c.current_step : 1) },
          { chave: 'envio', rotulo: 'Próximo envio', render: (c) => formatDateTime(c.next_send_at) },
          {
            chave: 'situacao',
            rotulo: 'Situação',
            render: (c) => (
              <Badge tone={c.status === 'active' ? 'ok' : 'neutral'}>
                {c.status === 'active' ? 'em andamento' : 'programado'}
              </Badge>
            ),
          },
        ]}
      />
    </Card>
  );
}

// ------------------------------------------------------------------ pausados

export function Pausados() {
  const { can } = useSession();
  const toast = useToast();
  const cycles = useResource<{ cycles: Cycle[] }>('/api/reminders/paused');
  const [retomando, setRetomando] = useState<Cycle | null>(null);

  return (
    <>
      <Alert tone="info" title="Nada volta a enviar sozinho">
        Ciclo pausado ou aguardando ação só retoma com uma nova data registrada por uma pessoa.
        Tempo, fim de conversa, cancelamento ou falta não reativam nada.
      </Alert>

      <Card tight>
        <ErrorBox error={cycles.error} />
        <Tabela
          linhas={cycles.data?.cycles ?? []}
          chave={(c) => c.id}
          vazio="Nenhum ciclo pausado ou aguardando ação."
          colunas={[
            { chave: 'cliente', rotulo: 'Cliente', tipo: 'principal', render: (c) => c.customer_name },
            {
              chave: 'situacao',
              rotulo: 'Situação',
              render: (c) => (
                <Badge tone={c.status === 'awaiting_action' ? 'critical' : 'attention'}>
                  {c.status === 'awaiting_action' ? 'aguardando ação' : 'pausado'}
                </Badge>
              ),
            },
            {
              chave: 'motivo',
              rotulo: 'Motivo',
              render: (c) =>
                MOTIVOS[c.pause_reason ?? c.awaiting_reason ?? ''] ??
                c.pause_reason ??
                c.awaiting_reason ??
                '—',
            },
            {
              chave: 'acoes',
              rotulo: '',
              tipo: 'actions',
              render: (c) =>
                can('reminders.write') ? (
                  <>
                    <button type="button" className="small" onClick={() => setRetomando(c)}>
                      Retomar
                    </button>
                    <button
                      type="button"
                      className="small danger"
                      onClick={async () => {
                        await post(`/api/reminders/cycles/${c.id}/cancel`, {
                          reason: 'cancelado no painel',
                        });
                        cycles.reload();
                        toast('Ciclo cancelado.');
                      }}
                    >
                      Cancelar
                    </button>
                  </>
                ) : null,
            },
          ]}
        />
      </Card>

      {retomando ? (
        <RetomarCiclo
          cycle={retomando}
          onClose={() => setRetomando(null)}
          onDone={() => {
            setRetomando(null);
            cycles.reload();
            toast('Ciclo reprogramado.');
          }}
        />
      ) : null}
    </>
  );
}

function RetomarCiclo({
  cycle,
  onClose,
  onDone,
}: {
  cycle: Cycle;
  onClose: () => void;
  onDone: () => void;
}) {
  const [localDate, setLocalDate] = useState('');
  const [reason, setReason] = useState('');
  const { submit, saving, error } = useSubmit(async () => {
    await post(`/api/reminders/cycles/${cycle.id}/resume`, { localDate, reason });
    onDone();
  });

  return (
    <Modal
      title={`Retomar lembrete de ${cycle.customer_name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="form-retomar" className="primary" disabled={saving}>
            {saving ? 'Salvando…' : 'Retomar nesta data'}
          </button>
        </>
      }
    >
      <form id="form-retomar" onSubmit={submit}>
        <ErrorBox error={error} />
        <p className="small muted">
          A retomada agenda uma única etapa na data escolhida. Nada atrasado é despejado.
        </p>
        <Field label="Nova data de envio" required>
          <input type="date" value={localDate} onChange={(e) => setLocalDate(e.target.value)} required />
        </Field>
        <Field label="Motivo" required hint="Fica registrado na auditoria.">
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} />
        </Field>
      </form>
    </Modal>
  );
}

// ------------------------------------------------------------------ histórico

interface Execution {
  id: string;
  step_number: number;
  status: string;
  scheduled_for: string;
  sent_at: string | null;
  rendered_body: string | null;
  skip_reason: string | null;
  customer_name: string;
}

const EXEC_STATUS: Record<string, { label: string; tone: 'neutral' | 'attention' | 'critical' | 'ok' }> = {
  sent: { label: 'na fila de envio', tone: 'attention' },
  accepted: { label: 'aceito pelo provedor', tone: 'ok' },
  failed: { label: 'falhou', tone: 'critical' },
  delivery_unknown: { label: 'entrega incerta', tone: 'critical' },
  skipped: { label: 'descartado', tone: 'neutral' },
  cancelled: { label: 'cancelado', tone: 'neutral' },
};

export function Historico() {
  const executions = useResource<{ executions: Execution[] }>('/api/reminders/history');

  return (
    <Card tight>
      <ErrorBox error={executions.error} />
      <Tabela
        linhas={executions.data?.executions ?? []}
        chave={(e) => e.id}
        vazio="Nenhum envio registrado ainda."
        colunas={[
          { chave: 'cliente', rotulo: 'Cliente', tipo: 'principal', render: (e) => e.customer_name },
          { chave: 'etapa', rotulo: 'Etapa', tipo: 'num', render: (e) => e.step_number },
          { chave: 'previsto', rotulo: 'Previsto', render: (e) => formatDateTime(e.scheduled_for) },
          {
            chave: 'situacao',
            rotulo: 'Situação',
            render: (e) => (
              <>
                <Badge tone={EXEC_STATUS[e.status]?.tone ?? 'neutral'}>
                  {EXEC_STATUS[e.status]?.label ?? e.status}
                </Badge>
                {e.skip_reason ? (
                  <div className="small muted">{MOTIVOS[e.skip_reason] ?? e.skip_reason}</div>
                ) : null}
              </>
            ),
          },
          {
            chave: 'mensagem',
            rotulo: 'Mensagem',
            render: (e) => <span className="small">{e.rendered_body ?? '—'}</span>,
          },
        ]}
      />
    </Card>
  );
}
